import * as THREE from 'three';
import { buildAuthorityTopologySnapshot } from './buildAuthorityTopologySnapshot';

/**
 * 兼容导出：特征拓扑顶点。
 */
export type FeatureTopologyVertex = TopologySnapshotVertex;

/**
 * 兼容导出：特征拓扑边。
 */
export type FeatureTopologyEdge = TopologySnapshotEdge;

/**
 * 兼容导出：特征拓扑面。
 */
export type FeatureTopologyFace = TopologySnapshotFace;

/**
 * 兼容导出：特征拓扑快照。
 */
export type FeatureTopologySnapshot = TopologySnapshot;

/**
 * 从几何体构建拓扑快照。
 *
 * 设计说明：
 * 1. 当前函数仅作为测试与非 worker 预览链的兼容入口；
 * 2. 它内部复用新的权威快照构建器，避免继续维护第二套拓扑推导逻辑；
 * 3. 真正的业务链路仍应优先消费 worker 随 `MeshData` 带来的权威快照。
 *
 * @param geometry 几何体。
 * @param faceTriangleRanges 面三角形范围。
 * @returns 特征拓扑快照；无法构建时返回 `null`。
 */
export function buildFeatureTopologySnapshot(
  geometry: THREE.BufferGeometry,
  faceTriangleRanges?: [number, number][],
): FeatureTopologySnapshot | null {
  const positionAttribute = geometry.getAttribute('position');
  if (!(positionAttribute instanceof THREE.BufferAttribute) || positionAttribute.count < 3) {
    return null;
  }

  const rawPositions = positionAttribute.array;
  const positions = rawPositions instanceof Float32Array
    ? rawPositions
    : new Float32Array(rawPositions);
  const indexAttribute = geometry.getIndex();
  const indices = indexAttribute instanceof THREE.BufferAttribute
    ? new Uint32Array(Array.from({ length: indexAttribute.count }, (_unused, index) => indexAttribute.getX(index)))
    : undefined;

  return buildAuthorityTopologySnapshot({
    positions,
    indices,
    faceTriangleRanges,
  });
}
